import React from 'react';
import { Box, FormControl, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import type { IntegrationSettings } from '../services/integrationApi';
import { SectionCard, StatusChip } from './ui';

export interface ShopifyListingShapeSectionProps {
  settings: IntegrationSettings;
  onChange: (partial: Partial<IntegrationSettings>) => void;
  /**
   * Whether the shop is on Shopify Plus, as recorded on the integration (never asked):
   * true, false, or null when the plan was never recorded.
   */
  shopifyPlus?: boolean | null;
}

/** A store path such as /collections/cameras: starts with one slash, no scheme or host. */
export const isValidRedirectPath = (value: string): boolean =>
  value === '' || (/^\/(?!\/)[^\s]*$/.test(value) && !/^\/*[a-z][a-z0-9+.-]*:/i.test(value));

const T = 'integrations.listingShape';

/**
 * One listing per color, linked as one product (Shopify combined listings, flag
 * `shopify_combined_listings`, MOT-602). Shown, never selectable: combined
 * listings are a Shopify Plus feature, so a store on any other plan sees that an
 * upgrade unlocks it, and a Plus store sees it as coming soon until Motzu can
 * push them (MOT-603). Nothing is stored.
 */
export const CombinedListingsOption: React.FC<{ shopifyPlus: boolean | null }> = ({ shopifyPlus }) => {
  const { t } = useAppTranslation();
  // A plan never recorded (null) is not known to be short of Plus, so it gets the neutral line.
  const status = shopifyPlus === true ? null : shopifyPlus === false ? t(`${T}.combined.requiresPlus`) : t(`${T}.combined.requiresPlusUnknown`);
  return (
    <Box
      aria-disabled="true"
      data-testid="combined-listings-option"
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5, bgcolor: 'action.hover' }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5, mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} color="text.secondary">
          {t(`${T}.combined.title`)}
        </Typography>
        <StatusChip
          tone={shopifyPlus === true ? 'info' : 'neutral'}
          label={shopifyPlus === true ? t(`${T}.combined.comingSoon`) : t(`${T}.combined.requiresPlusShort`)}
        />
      </Stack>
      <Typography variant="caption" color="text.secondary" component="div">
        {t(`${T}.combined.description`)}
      </Typography>
      {status && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }} data-testid="combined-listings-status">
          {status}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Listing shape (flag `shopify_listing_shape`, Estimate row B37): how a new
 * Shopify listing is structured and what happens to a Motzu-created listing
 * when its product leaves the sync selection. With `shopify_combined_listings`
 * the card also shows the disabled combined-listings layout, on its own when
 * listing shape is off. Hidden when both flags are off.
 */
const ShopifyListingShapeSection: React.FC<ShopifyListingShapeSectionProps> = ({ settings, onChange, shopifyPlus = null }) => {
  const { t } = useAppTranslation();
  const { enabled } = useOrgFeatureFlag('shopify_listing_shape');
  const { enabled: combinedEnabled } = useOrgFeatureFlag('shopify_combined_listings');
  if (!enabled && !combinedEnabled) return null;
  if (!enabled) {
    return (
      <SectionCard title={t(`${T}.title`)} subtitle={t(`${T}.combined.cardSubtitle`)} dense padded>
        <CombinedListingsOption shopifyPlus={shopifyPlus} />
      </SectionCard>
    );
  }

  const structure = settings.listingStructure || 'variants';
  const onUnsync = settings.onUnsync || 'leave';
  const redirect = settings.unsyncRedirectTo || '';
  const redirectInvalid = !isValidRedirectPath(redirect);

  return (
    <SectionCard title={t(`${T}.title`)} subtitle={t(`${T}.subtitle`)} dense padded>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {t(`${T}.structure.label`)}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 260, maxWidth: '100%' }}>
            <Select
              value={structure}
              onChange={(e) => onChange({ listingStructure: e.target.value as 'individual' | 'variants' })}
              inputProps={{ 'aria-label': t(`${T}.structure.label`) }}
            >
              <MenuItem value="variants">{t(`${T}.structure.variants`)}</MenuItem>
              <MenuItem value="individual">{t(`${T}.structure.individual`)}</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
            {t(`${T}.structure.desc`)}
          </Typography>
          {combinedEnabled && (
            <Box sx={{ mt: 1.5 }}>
              <CombinedListingsOption shopifyPlus={shopifyPlus} />
            </Box>
          )}
        </Box>

        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {t(`${T}.onUnsync.label`)}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 260, maxWidth: '100%' }}>
            <Select
              value={onUnsync}
              onChange={(e) => onChange({ onUnsync: e.target.value as 'leave' | 'draft' | 'archive' })}
              inputProps={{ 'aria-label': t(`${T}.onUnsync.label`) }}
            >
              <MenuItem value="leave">{t(`${T}.onUnsync.leave`)}</MenuItem>
              <MenuItem value="draft">{t(`${T}.onUnsync.draft`)}</MenuItem>
              <MenuItem value="archive">{t(`${T}.onUnsync.archive`)}</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
            {t(`${T}.onUnsync.desc`)}
          </Typography>

          {onUnsync !== 'leave' && (
            <Box sx={{ mt: 1.5 }}>
              <TextField
                size="small"
                fullWidth
                label={t(`${T}.redirect.label`)}
                placeholder="/collections/cameras"
                value={redirect}
                error={redirectInvalid}
                helperText={redirectInvalid ? t(`${T}.redirect.invalid`) : t(`${T}.redirect.desc`)}
                onChange={(e) => onChange({ unsyncRedirectTo: e.target.value.trim() })}
                sx={{ maxWidth: 420 }}
              />
            </Box>
          )}
        </Box>
      </Stack>
    </SectionCard>
  );
};

export default ShopifyListingShapeSection;
